import { forwardRef } from 'react';
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';

import classNames from '@/utils/classnames';

/**
 * Styled wrapper around Radix ContextMenu, sharing the same surface recipe as
 * DropdownMenu so right-click menus look identical to the app's other menus.
 */
export const ContextMenu = ContextMenuPrimitive.Root;
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
export const ContextMenuGroup = ContextMenuPrimitive.Group;
export const ContextMenuPortal = ContextMenuPrimitive.Portal;

export const ContextMenuContent = forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={classNames(
        'z-dropdown min-w-48 rounded-xl border border-line-strong bg-surface-1 p-1.5 shadow-(--shadow-overlay) animate-fade-in',
        className,
      )}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
));
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;

export const ContextMenuItem = forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & { destructive?: boolean }
>(({ className, destructive, ...props }, ref) => (
  <ContextMenuPrimitive.Item
    ref={ref}
    className={classNames(
      'flex h-8 cursor-pointer select-none items-center gap-2.5 rounded-lg px-2 text-xs outline-none transition-colors',
      'data-disabled:pointer-events-none data-disabled:opacity-50',
      destructive
        ? 'text-danger data-highlighted:bg-danger-tint'
        : 'text-fg-secondary data-highlighted:bg-surface-3 data-highlighted:text-fg',
      className,
    )}
    {...props}
  />
));
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName;

export function ContextMenuSeparator({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>) {
  return <ContextMenuPrimitive.Separator className={classNames('my-1 h-px bg-line-subtle', className)} {...props} />;
}
